import type { SearchFilterDef, SearchSchema } from '../api/types';

/**
 * The search form as it was hand-built before GET /api/search/schema existed.
 *
 * Used only when the schema endpoint fails or isn't deployed yet, so the
 * "Find creators" form keeps working against an older discovery server. Every
 * key is a ScrapeParams key, and the defaults are the old form's INITIAL
 * values (which the server's Settings now own, contract §1).
 *
 * When the server schema ships, this stays as the offline fallback; it does
 * not need to track every server-side tweak.
 */

const ALL_X_LANGS = ['ar', 'fa', 'hi', 'ur', 'en', 'es', 'pt', 'in', 'tl', 'th', 'vi', 'tr', 'fr', 'de', 'nl', 'ja', 'ko'];

function langLabel(code: string): string {
  try {
    return new Intl.DisplayNames(['en'], { type: 'language' }).of(code === 'in' ? 'id' : code === 'tl' ? 'fil' : code) ?? code;
  } catch {
    return code;
  }
}

const TIKTOK_KEYWORD = { platforms: ['tiktok' as const], modes: ['keyword'] };
const TIKTOK_SOUND = { platforms: ['tiktok' as const], modes: ['sound'] };

const filters: SearchFilterDef[] = [
  // ── Search ─────────────────────────────────────────────────────────────
  { key: 'targetCount', label: 'Target', group: 'search', type: 'integer', min: 1, max: 2000, unit: 'creators', help: 'Stop once this many creators are saved' },
  {
    key: 'xSearchType', label: 'Search', group: 'search', type: 'select', platforms: ['x'], modes: ['keyword'],
    options: [
      { value: 'Videos', label: 'Videos only' },
      { value: 'Top', label: 'Top posts' },
      { value: 'Latest', label: 'Latest posts' },
    ],
  },
  {
    key: 'xLang', label: 'Post language', group: 'search', type: 'select', platforms: ['x'], modes: ['keyword'], help: "X's lang: operator",
    options: [{ value: '', label: 'Any' }, ...ALL_X_LANGS.map((l) => ({ value: l, label: langLabel(l) }))],
  },
  { key: 'xMinFaves', label: 'Min likes per post', group: 'search', type: 'integer', min: 0, platforms: ['x'], modes: ['keyword'] },
  {
    key: 'soundSnowball', label: 'Keep finding sounds', group: 'search', type: 'boolean', ...TIKTOK_SOUND,
    help: 'Each sound runs out after a few hundred creators. Keep going through the sounds the creators you keep also use, most-shared first. Works best with On camera set to Face creators only.',
  },

  // ── Filters (always in view) ───────────────────────────────────────────
  { key: 'minFollowers', label: 'Min followers', group: 'filters', type: 'integer', min: 0, unit: 'followers' },
  { key: 'maxFollowers', label: 'Max followers', group: 'filters', type: 'integer', min: 0, unit: 'followers' },
  { key: 'minAvgViews', label: 'Min avg views', group: 'filters', type: 'integer', min: 0, unit: 'views' },
  { key: 'maxAvgViews', label: 'Max avg views', group: 'filters', type: 'integer', min: 0, unit: 'views' },
  { key: 'maxLastPostedDays', label: 'Posted within (days)', group: 'filters', type: 'integer', min: 1, unit: 'days' },

  // ── Topic ──────────────────────────────────────────────────────────────
  {
    key: 'topicTerms', label: 'Only keep creators posting about', group: 'topic', type: 'text', list: true, placeholder: 'anime, manga',
    help: 'Comma separated, any one counts. Blank = your search words, minus format and place words.',
  },
  { key: 'minTopicMatch', label: 'Min topic match', group: 'topic', type: 'number', min: 0, max: 1, step: 0.1, help: 'Share of recent posts on topic, 0–1. 0.3 ≈ 6 of 20' },

  // ── Audience & location ────────────────────────────────────────────────
  {
    key: 'audienceFilter', label: 'Only keep creators whose audience is in…', group: 'geo', type: 'boolean',
    help: 'Reads where each creator\'s commenters (TikTok) or repliers (X) are from. Instagram uses a small monthly audience-data quota.',
    costHint: '2–4 extra API calls per creator that passes the other filters',
  },
  { key: 'audienceCountries', label: 'Audience countries', group: 'geo', type: 'countryList', dependsOn: 'audienceFilter', help: 'Any country, or a group like GCC' },
  { key: 'minAudienceShare', label: 'Min audience there', group: 'geo', type: 'percent', min: 0, max: 1, dependsOn: 'audienceFilter' },
  {
    key: 'xVerifyAudience', label: "Confirm with X's own account location", group: 'geo', type: 'boolean', platforms: ['x'], dependsOn: 'audienceFilter',
    help: 'Separates the UAE from Saudi Arabia, which profile text can\'t.', costHint: '~20 extra calls per creator against X\'s monthly quota',
  },
  { key: 'creatorCountries', label: 'Creator based in', group: 'geo', type: 'countryList', help: 'Where they post from. Unknown locations are skipped' },
  { key: 'contentLanguages', label: 'Content language', group: 'geo', type: 'languageList', help: 'Read from their captions and bio. Free' },
  { key: 'minLanguageShare', label: 'Posts in these languages', group: 'geo', type: 'percent', min: 0, max: 1, dependsOn: 'contentLanguages' },

  // ── Account type ───────────────────────────────────────────────────────
  {
    key: 'pageFilter', label: 'Pages or people', group: 'account', type: 'select', help: 'From name, bio, category and posting rate. Free',
    options: [
      { value: 'any', label: 'Anyone' },
      { value: 'pages', label: 'Pages only (theme, news, memes, clips)' },
      { value: 'people', label: 'People only' },
    ],
  },
  {
    key: 'presenterFilter', label: 'On camera', group: 'account', type: 'select', help: 'Reads recent thumbnails', costHint: 'A small vision call per creator',
    options: [
      { value: 'any', label: 'Anyone' },
      { value: 'faces', label: 'Face creators only' },
      { value: 'faceless', label: 'Faceless only (clips, edits)' },
    ],
  },
  { key: 'minPostsPerDay', label: 'Min posts per day', group: 'account', type: 'number', min: 0, step: 0.5, platforms: ['x'], help: 'Pages post often' },
  {
    key: 'igAccountKinds', label: 'Instagram account', group: 'account', type: 'multiselect', platforms: ['instagram'],
    options: [
      { value: 'professional', label: 'Creator / business' },
      { value: 'personal', label: 'Personal' },
    ],
  },
  {
    key: 'excludeOfficial', label: 'Skip official & gossip accounts', group: 'account', type: 'boolean',
    help: 'Broadcasters and news pages out-rank every creator on followers and views. With "Pages only", only brand accounts are skipped.',
  },
  { key: 'verifiedOnly', label: 'Verified only', group: 'account', type: 'boolean' },

  // ── Profile & contact ──────────────────────────────────────────────────
  { key: 'minVideoCount', label: 'Min video count', group: 'profile', type: 'integer', min: 0 },
  { key: 'maxVideoCount', label: 'Max video count', group: 'profile', type: 'integer', min: 0 },
  { key: 'minEngagementRate', label: 'Min engagement', group: 'profile', type: 'number', min: 0, step: 0.1, unit: '%', help: 'Median (likes + comments + shares) / views' },
  {
    key: 'emailFilter', label: 'Email', group: 'profile', type: 'select',
    options: [
      { value: 'any', label: 'Any' },
      { value: 'any-email', label: 'Has email' },
      { value: 'none', label: 'No email' },
      { value: 'agency', label: 'Agency only' },
      { value: 'generic', label: 'Generic only' },
    ],
  },
  { key: 'bioKeywords', label: 'Bio keywords', group: 'profile', type: 'text', placeholder: 'editor, clips', help: 'Comma separated; any one is enough' },
  { key: 'requireBioLink', label: 'Has link in bio', group: 'profile', type: 'boolean' },

  // ── Search strategy ────────────────────────────────────────────────────
  {
    key: 'maxReach', label: 'Max reach', group: 'strategy', type: 'boolean', ...TIKTOK_KEYWORD,
    help: 'Keeps finding new creators after TikTok\'s keyword search runs out (~100), through related hashtags, sounds, term variants and regional result sets. Starts as a normal search, so small searches cost the same.',
  },
  {
    key: 'sortType', label: 'Sort by', group: 'strategy', type: 'select', ...TIKTOK_KEYWORD,
    options: [
      { value: 0, label: 'Relevance' },
      { value: 1, label: 'Like count' },
      { value: 3, label: 'Date posted' },
    ],
  },
  {
    key: 'publishTime', label: 'Videos posted within', group: 'strategy', type: 'select', ...TIKTOK_KEYWORD,
    help: 'Keyword results only. Use "Posted within (days)" to require recent posts.',
    options: [
      { value: 0, label: 'All time' },
      { value: 1, label: 'Past 24 hours' },
      { value: 7, label: 'This week' },
      { value: 30, label: 'This month' },
      { value: 90, label: 'Last 3 months' },
      { value: 180, label: 'Last 6 months' },
    ],
  },
  {
    key: 'rotateSorts', label: 'Rotate sort types', group: 'strategy', type: 'boolean', ...TIKTOK_KEYWORD, hiddenWhen: 'maxReach',
    help: 'Runs each keyword by relevance, likes and date. Adds few new creators; Max reach finds far more.',
  },
  { key: 'rotateTimeWindows', label: 'Rotate time windows', group: 'strategy', type: 'boolean', ...TIKTOK_KEYWORD, hiddenWhen: 'maxReach' },
  {
    key: 'allowDuplicates', label: 'Allow creators from other folders', group: 'strategy', type: 'boolean',
    help: 'Creators already in outreach or on the platform are always skipped.',
  },
  { key: 'includeDismissed', label: 'Include dismissed creators', group: 'strategy', type: 'boolean', help: 'Re-surface creators an outreacher previously dismissed.' },
];

export const FALLBACK_SCHEMA: SearchSchema = {
  platforms: [
    {
      id: 'tiktok',
      label: 'TikTok',
      available: true,
      modes: [
        { id: 'keyword', label: 'Keyword', queryLabel: 'Keywords', queryPlaceholder: 'love island edit\nreality tv clips', multi: true, hint: 'Videos matching words. Add place or dialect words for a country ("dubai memes").' },
        { id: 'hashtag', label: 'Hashtag', queryLabel: 'Hashtags', queryPlaceholder: 'dubaicomedy\nميمز', multi: true, hint: 'Everyone posting under a tag. Pages deeper than keyword search.' },
        { id: 'sound', label: 'Sound', queryLabel: 'TikTok sounds', queryPlaceholder: 'Paste a sound link to add it…', multi: true, hint: 'Everyone using a sound. Best for lipsync and dance.' },
      ],
    },
    {
      id: 'instagram',
      label: 'Instagram',
      available: true,
      modes: [
        { id: 'similar', label: 'Similar accounts', queryLabel: 'Seed accounts', queryPlaceholder: 'khalidalameri\nhttps://www.instagram.com/lovindubai/', multi: true, hint: "Instagram's own related accounts for each seed. Stays in the seed's region and niche — the strongest option here." },
        { id: 'sound', label: 'Audio', queryLabel: 'Instagram audio', queryPlaceholder: 'https://www.instagram.com/reels/audio/2402308286888372/', multi: true, hint: 'Reels using an audio, with their creators and plays. Best for reel creators.' },
        { id: 'keyword', label: 'Accounts', queryLabel: 'Keywords', queryPlaceholder: 'dubai food\ntehran vlog', multi: true, hint: 'Accounts whose name matches. Latin script only.' },
        { id: 'hashtag', label: 'Hashtag', queryLabel: 'Hashtags', queryPlaceholder: 'dubaicomedy', multi: true, hint: 'Posters under a tag. Mostly photos and businesses — the weakest source.' },
      ],
    },
    {
      id: 'x',
      label: 'X',
      available: true,
      modes: [
        { id: 'people', label: 'Accounts', queryLabel: 'Describe the accounts', queryPlaceholder: 'tech news\nclips', multi: true, hint: 'Accounts whose handle, name or bio match — finds pages by how they describe themselves ("tech news", "clips").' },
        { id: 'lists', label: 'Lists', queryLabel: 'List topics', queryPlaceholder: 'tech news\nAI\ngadgets', multi: true, hint: "Members of X's own curated lists on a topic. The densest source of theme pages." },
        { id: 'keyword', label: 'Posts', queryLabel: 'Keywords', queryPlaceholder: 'podcast clips\nmemes', multi: true, hint: 'Whoever posted matching videos. Supports X operators like lang: and min_faves:.' },
        { id: 'similar', label: 'Similar accounts', queryLabel: 'Seed accounts', queryPlaceholder: 'elonmusk', multi: true, hint: '"Who to follow" suggestions X shows on each seed\'s profile.' },
      ],
    },
  ],
  filters,
  groups: [
    { id: 'search', label: 'Search', defaultOpen: true },
    { id: 'filters', label: 'Filters', defaultOpen: true },
    { id: 'topic', label: 'Topic', defaultOpen: false },
    { id: 'geo', label: 'Audience & location', defaultOpen: false },
    { id: 'account', label: 'Account type', defaultOpen: false },
    { id: 'profile', label: 'Profile & contact', defaultOpen: false },
    { id: 'strategy', label: 'Search strategy', defaultOpen: false },
  ],
  defaults: {
    targetCount: 100,
    xSearchType: 'Videos',
    xLang: '',
    // On for new searches: it starts as a normal search, so a search that
    // fills its target early costs exactly the same.
    maxReach: true,
    sortType: 0,
    publishTime: 0,
    emailFilter: 'any',
    pageFilter: 'any',
    // Off by default — the one filter that costs money per creator.
    presenterFilter: 'any',
    // On by default: broadcasters and gossip pages beat every creator on
    // followers and views, so numeric filters promote them rather than cull them.
    excludeOfficial: true,
    // Off by default: it adds API calls per creator, so a search opts in.
    audienceFilter: false,
    minAudienceShare: 0.3,
    minLanguageShare: 0.5,
  },
};
